/** Embeddable date&time window panel — the console's one time-range editor.
 *
 * Relative mode: "last N" (and "next N" where the field looks forward) windows; a preset
 * applies on click. Absolute mode: typed From/To fields to the second, an inline
 * calendar (one click = that whole day), shortcuts, and a live "Now" for To — edited in
 * UTC (default) or the browser's zone.
 *
 * The panel emits TimeBounds. A relative pick stays relative (`now-15m`, resolved by the
 * backend per request) unless the consumer passes keepRelative={false}, which freezes it
 * to instants at Apply. Consumers format the result for their wire (QuerySearch →
 * boundToNaive, toolbar URLs → boundToIso).
 */
import { Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { useState } from 'react';

import { AbsolutePane, type AbsoluteTo } from './AbsolutePane';
import { RelativePane, type RelativeWindow } from './RelativePane';
import { isNow, isRelative, NOW, relativeMinute, resolveTimeWindow } from './relative';
import type { TimeBound, TimeWindowPanelProps, TimeWindowTz, TimeWindowValue } from './types';

/** The preset shape a value has, if any: [now−N, now] or [now, now+N]. */
function asRelativeWindow(
  from: TimeBound | null,
  to: TimeBound | null,
  allowFuture: boolean,
): RelativeWindow | null {
  if (isRelative(from) && from.offset < 0 && isNow(to)) {
    return { direction: 'last', amount: -from.offset, unit: from.unit };
  }
  if (allowFuture && isNow(from) && isRelative(to) && to.offset > 0) {
    return { direction: 'next', amount: to.offset, unit: to.unit };
  }
  return null;
}

/** A relative bound shown in Absolute mode is pinned to where it falls now; a relative
 *  To other than `now` shows the last second of its minute (what it covers). */
function absoluteFrom(b: TimeBound | null): Date | null {
  if (b === null) return null;
  return isRelative(b) ? relativeMinute(b) : b;
}

function absoluteTo(b: TimeBound | null, allowLive: boolean): AbsoluteTo {
  if (b === null) return null;
  if (!isRelative(b)) return b;
  if (allowLive && isNow(b)) return 'now';
  return new Date(relativeMinute(b).getTime() + 59_000);
}

export function TimeWindowPanel({
  initialFrom = null,
  initialTo = null,
  defaultTz = 'utc',
  defaultMode,
  requireBothBounds = true,
  allowFuture = false,
  keepRelative = true,
  tz: tzControlled,
  onTzChange,
  onClear,
  onApply,
}: TimeWindowPanelProps) {
  const selected = asRelativeWindow(initialFrom, initialTo, allowFuture);
  const empty = initialFrom === null && initialTo === null;
  const [mode, setMode] = useState<'relative' | 'absolute'>(
    defaultMode ?? (selected || empty ? 'relative' : 'absolute'),
  );
  const [tzState, setTzState] = useState<TimeWindowTz>(tzControlled ?? defaultTz);
  const tz = tzControlled ?? tzState;

  const switchTz = (next: TimeWindowTz | null) => {
    if (!next || next === tz) return;
    setTzState(next);
    onTzChange?.(next);
  };

  const emit = (value: TimeWindowValue) => onApply(keepRelative ? value : resolveTimeWindow(value));

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ py: 0.5, width: 560, maxWidth: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_, v) => v && setMode(v as typeof mode)}
            aria-label="Window kind"
          >
            <ToggleButton value="relative" sx={{ px: 1.25, py: 0.25 }}>
              Relative
            </ToggleButton>
            <ToggleButton value="absolute" sx={{ px: 1.25, py: 0.25 }}>
              Absolute
            </ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flexGrow: 1 }} />
          {mode === 'absolute' && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={tz}
              onChange={(_, v) => switchTz(v as TimeWindowTz | null)}
              aria-label="Timezone"
            >
              <ToggleButton value="utc" sx={{ px: 1.25, py: 0.25 }}>
                UTC
              </ToggleButton>
              <ToggleButton value="local" sx={{ px: 1.25, py: 0.25 }}>
                Local
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          {onClear && (
            <Button size="small" onClick={onClear} aria-label="Clear time window">
              Clear
            </Button>
          )}
        </Box>

        {mode === 'relative' ? (
          <RelativePane
            selected={selected}
            allowFuture={allowFuture}
            live={keepRelative}
            onApply={(w) =>
              emit(
                w.direction === 'last'
                  ? [{ offset: -w.amount, unit: w.unit }, NOW]
                  : [NOW, { offset: w.amount, unit: w.unit }],
              )
            }
          />
        ) : (
          <AbsolutePane
            initialFrom={absoluteFrom(initialFrom)}
            initialTo={absoluteTo(initialTo, keepRelative)}
            tz={tz}
            requireBothBounds={requireBothBounds}
            allowLive={keepRelative}
            onApply={(from, to) => emit([from, to === 'now' ? NOW : to])}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
}
